import { useState } from 'react';
import { useChartContext, COLOR_VISION_SINGLE_ITEM, BARS_TRANSITION_CONFIG, getRoundedRectPath, getColorVisionStylesForActiveIndex, STACKED_BAR_GAP, getColorVisionEventAttrs } from '@shopify/polaris-viz-core';
import { useSpring, animated } from '@react-spring/web';
import styles from './Stack.scss.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useWatchColorVisionEvents } from '../../../../../../hooks/ColorVisionA11y/useWatchColorVisionEvents.js';
import { getBorderRadiusForStackedValues } from '../../../../utilities/getBorderRadiusForStackedValues.js';
import { getYPosition } from '../../../../utilities/getYPosition.js';
import { getGradientDefId } from '../../../../../shared/GradientDefs/GradientDefs.js';

function Stack({
  activeBarGroup,
  animationDelay,
  data,
  gaps,
  groupIndex,
  id,
  width,
  x,
  yScale
}) {
  const [activeBarIndex, setActiveBarIndex] = useState(-1);
  const {
    theme,
    shouldAnimate
  } = useChartContext();
  const keys = data[0] ? Object.keys(data[0].data) : [];
  useWatchColorVisionEvents({
    type: COLOR_VISION_SINGLE_ITEM,
    onIndexChange: ({
      detail
    }) => {
      if (detail.index === -1 || activeBarGroup === -1 || activeBarGroup === groupIndex) {
        setActiveBarIndex(detail.index);
      }
    }
  });
  const {
    transform
  } = useSpring({
    from: {
      transform: `scale(1, 0)`
    },
    to: {
      transform: `scale(1, 1)`
    },
    config: BARS_TRANSITION_CONFIG,
    delay: shouldAnimate ? animationDelay : 0,
    default: {
      immediate: !shouldAnimate
    }
  });
  return /*#__PURE__*/jsx(animated.g, {
    style: {
      transform,
      transformOrigin: `0px ${yScale(0)}px`
    },
    children: data.map((data, index) => {
      const [start, end] = data;
      const height = Math.abs(yScale(end) - yScale(start));
      const values = data.data ? Object.values(data.data) : [];
      const ariaLabel = `${keys[index]} ${values[index]}`;
      const pathD = getRoundedRectPath({
        height,
        width,
        borderRadius: getBorderRadiusForStackedValues(values, index)
      });
      const y = getYPosition({
        start,
        end,
        groupIndex: index,
        gaps: gaps[groupIndex],
        yScale
      });
      return /*#__PURE__*/jsxs("g", {
        "aria-hidden": "true",
        children: [/*#__PURE__*/jsx("path", {
          fill: `url(#${getGradientDefId(theme, index, id)})`,
          d: pathD,
          transform: `translate(${x},${y})`,
          style: getColorVisionStylesForActiveIndex({
            activeIndex: activeBarIndex,
            index
          }),
          "aria-hidden": "true"
        }, index), /*#__PURE__*/jsx("rect", {
          className: styles.Bar,
          fill: "transparent",
          height: height + STACKED_BAR_GAP,
          width: width,
          transform: `translate(${x},${y})`,
          ...getColorVisionEventAttrs({
            type: COLOR_VISION_SINGLE_ITEM,
            index
          }),
          tabIndex: -1,
          role: "listitem",
          "aria-label": ariaLabel
        })]
      }, `${groupIndex}-${index}`);
    })
  });
}

export { Stack };
